/**
 * learningProfile.js — MOAT 1.0 Phase 4 · ONE canonical read-only LearningProfile.
 * Aggregates EXISTING stores (profileStore, mastery, skillLedger, TTQS, English, reading,
 * journey, focus, recent activity, parent goals, learning memory). Writes nothing.
 * Non-PII beyond the optional first name already held locally.
 */
import { getActiveProfile } from "@/lib/profileStore";
import { getActiveChild } from "@/lib/children";
import { loadProgress, worldPoints } from "@/lib/progress";
import { loadMastery, masteredCount } from "@/lib/mastery";
import { getAllSkills, getWeakSkills } from "@/lib/skillLedger";
import { reviewDue, stillGrowingCodes } from "@/lib/reuseEngine";
import { ttqsState, englishState, journeyWeek } from "@/lib/homeAdventure";
import { worldStats as ttqsWorldStats } from "@/lib/ttqs";
import { getReadingLevel } from "@/lib/readingLevel";
import { recentAdventures } from "@/lib/recentAdventures";
import { recentGames } from "@/lib/gameCatalog";
import { loadFocus } from "@/lib/focusDay";
import { loadGoals } from "@/lib/parentGoals";
import { memoryTags } from "@/lib/learningMemory";
import { rankSnapshot } from "@/lib/explorerRank";
import { listEntries } from "@/lib/portfolio";
import { bandForAge, yearForAge, JOURNEY_WEEKS } from "@/lib/canonical";

const safe = (fn, fb) => { try { return fn(); } catch { return fb; } };

export function getLearningProfile() {
  const prof = getActiveProfile();
  const child = getActiveChild();
  const p = loadProgress();
  const mastery = loadMastery();
  const ledger = getAllSkills() || {};
  const age = prof.age;
  const goals = loadGoals();
  const focus = loadFocus();
  const recent = recentAdventures(8);
  const ttqs = safe(() => ttqsState(), { status: "new" });
  const ttqsStats = safe(() => ttqsWorldStats(), {});
  const english = safe(() => englishState(), { status: "not_started", skills: [] });
  const reading = { level: safe(() => getReadingLevel(), null), history: (child?.readingHistory || []).length };
  const attempts = Object.values(mastery).filter((r) => r.attempts?.length);
  const interests = [...new Set([...(child?.subjects || []), ...(goals.map((g) => g.subject).filter(Boolean))])];
  return {
    childId: prof.childId, name: prof.name, age, ageBand: bandForAge(age), year: yearForAge(age), curriculum: prof.curriculum,
    subjects: prof.subjects, interests,
    currentObjectives: [...stillGrowingCodes(), ...reviewDue().map((d) => d.code)].slice(0, 6),
    completedObjectives: Object.entries(mastery).filter(([, r]) => r.mastered).map(([c]) => c),
    skills: { practised: attempts.length + Object.keys(ledger).length, mastered: masteredCount(), growing: attempts.filter((r) => !r.mastered).length, weak: getWeakSkills({ limit: 5 }).map((w) => w.skillId) },
    practiceNeeds: { review: reviewDue().length, growing: stillGrowingCodes().length, ttqsWeak: ttqsStats.weak || 0, ttqsDue: ttqsStats.due || 0 },
    assessmentEvidence: { attempts: attempts.reduce((n, r) => n + r.attempts.length, 0), portfolio: listEntries().length, englishAssessed: english.skills?.length || 0 },
    TTQSState: ttqs, EnglishState: english, readingState: reading,
    journeyWeek: journeyWeek(), journeyTotal: JOURNEY_WEEKS,
    focusHistory: focus ? [{ subject: focus.subject, year: focus.year, startedAt: focus.startedAt, done: (focus.done || []).length }] : [],
    recentActivity: recent, recentGames: recentGames(3).map((g) => g.id),
    availableMinutes: child?.dailyMinutes || prof.dailyMinutes || 15, availableDays: goals[0]?.days || null,
    preferredActivityTypes: memoryTags().filter((t) => t.startsWith("prefers:")).map((t) => t.slice(8)),
    parentGoals: goals, memory: memoryTags(),
    rewards: { stars: p.stars || 0, points: worldPoints(p), places: (p.countriesLearned || []).length, badges: (p.badges || []).length, rank: rankSnapshot() },
    isNew: !(p.activity || []).length && !(p.countriesLearned || []).length && ttqs.status === "new" && english.status === "not_started",
  };
}

/** Compact, non-PII context for the AI orchestrator (Phase 5) — no name, no free text. */
export function profileForAI(profile = getLearningProfile()) {
  const { name, childId, recentActivity, ...rest } = profile;
  return { ...rest, recentRoutes: recentActivity.map((r) => r.to).slice(0, 5) };
}
